import {
  makeCloudWorkspaceConnectionGateway,
  CloudWorkspaceConnectionGateway,
} from "@afar-research/client-runtime/connection";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import { isPageUnused } from "../hooks/usePageUnused";
import { readLocalApi } from "../localApi";
import { reconcileConnectWorkspaceOutcome } from "./connectSnapshot";

/**
 * Reaching a Linked Computer through AFAR Connect (ADR-0122).
 *
 * Every surface holding an account can do this, unlike an Environment Forward, which only
 * Desktop could open. The bridge is absent when no account is configured, and the gateway's own
 * default then refuses rather than appearing able to connect.
 *
 * Connect starts a stopped workspace for any introduction, so a page nobody is at never asks for
 * one. Otherwise the run that just stopped because this page went unused would be booted again by
 * the reconnect that follows its closing socket, before the page lets go of the connection. The
 * refusal is answered here, without a request, and the next use of the page connects again.
 *
 * A hidden page does not ask either. Every introduction reaches Connect's database, so a tab in
 * the background retrying a workspace that is starting or unreachable would keep that database
 * from ever pausing. Becoming visible again wakes the supervisor, which connects at once.
 */
export const cloudWorkspaceConnectionGatewayLayer = Layer.effect(
  CloudWorkspaceConnectionGateway,
  Effect.sync(() => {
    const connect = readLocalApi()?.connect;
    return connect === undefined
      ? CloudWorkspaceConnectionGateway.defaultValue()
      : makeCloudWorkspaceConnectionGateway(async (request) => {
          if (isPageUnused() || document.visibilityState === "hidden")
            return { _tag: "CloudWorkspaceRefused", code: "page_unused", status: 503 };
          const outcome = await connect.connectEnvironment(request);
          reconcileConnectWorkspaceOutcome(request.environmentId, outcome);
          return outcome;
        });
  }),
);
